import { useState, useEffect, useMemo, useRef } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { DuesRatesRow, Variables, ContextType } from "./types";
import { DuesRates, PreviousPricing } from "@/types";
import { useQuery } from "@apollo/client";
import { GET_DUES_RATES_QUERY, GET_MANAGE_DUES_RATES_QUERY } from "./queries";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { sortBy, orderBy as orderByLodash } from "lodash";
import { ColumnSort } from "@tanstack/react-table";
import { Updater } from "@tanstack/table-core";
import SearchInput from "@/components/SearchInput";
import { useSearchWithParams, useParams } from "@/hooks/useSearchParams";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ClassificationType } from "../../types";
import MultiSelect from "@/components/MultiSelect";
import { get } from "lodash";
import { AdjustRateModal } from "./_components/AdjustRateModal";
import { FinalizePricingModal } from "./_components/FinalizePricingModal";
import { Cell } from "@tanstack/react-table";
import { EditRateModal } from "./_components/EditRateModal";
import { getColumns } from "./_components/TableColumns";
import { UPDATE_DUES_RATES_MUTATION, DUES_FINALIZE_RATES_MUTATION } from "./queries";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useOutletContext } from "react-router-dom";
import { DOMAIN, PERMISSION, useHasCapabilityBool, useRequireCapability } from "@/hooks/useCapabilities";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { filterErrorsByKeys, getStatusById } from "./_utils";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

type DuesRatesData = {
  duesRates: {
    nodes: DuesRates[];
  };
};

const getPriceByYear = (previousPricing: PreviousPricing[], year: string) => {
  return centsToDollarsFormatted(previousPricing?.filter((p) => p.year === year)?.[0]?.priceCents);
};

const formatTableData = ({
  data,
  errors = [],
  warnings = [],
  currentYear
}: {
  data: DuesRates[];
  errors: Record<string, string[]>[];
  warnings: Record<string, string[]>[];
  currentYear: number;
}) => {
  return data.map(
    ({
      id,
      classification,
      classificationDescription,
      finalized,
      productName,
      memberGrade,
      updatedAt,
      priceCents,
      previousPricing
    }) => ({
      id,
      productName: `${memberGrade} ${productName} `,
      classification,
      classificationDescription,
      updatedAt: toFormat(updatedAt),
      rate_current_year: centsToDollarsFormatted(priceCents),
      rate_previous_year: getPriceByYear(previousPricing, `${currentYear - 1}`),
      rate_previous_year_2: getPriceByYear(previousPricing, `${currentYear - 2}`),
      classificationNumber: Number(classification?.split("-")?.[1]),
      classificationLetter: classification?.split("-")?.[0],
      status: getStatusById(errors, warnings, id).status,
      statusMessage: getStatusById(errors, warnings, id).statusMessage,
      finalized
    })
  );
};

export default function AnnualNationalRatesPage() {
  const { trackEvent } = useAmplitude();
  usePageTitle(
    "/billing/dues-management/manage-products/annual-national-rates",
    "Billing | Dues Management | Manage Products | Annual National Rates"
  );
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewDuesRate });
  const defaultVariables = {
    kind: "national"
  };
  const { data: manageDuesRatesData } = useQuery(GET_MANAGE_DUES_RATES_QUERY, {
    variables: {
      filters: {
        ...defaultVariables
      }
    }
  });
  const canManageDuesRates = useHasCapabilityBool({
    domain: DOMAIN.COMMERCE,
    permission: PERMISSION.manageDuesRates
  });

  const { canEdit, canFinalize } = get(manageDuesRatesData, "manageDuesRates", {});
  const { isCompactLayout } = useIsCompactLayout();

  const { setErrorMessage } = useOutletContext<ContextType>();
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const [variables, setVariables] = useState<Variables>({ ...defaultVariables });
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.classifications || []
  );
  const defaultSort = [{ id: "classification", desc: false }];
  const [orderBy, setOrderBy] = useState<ColumnSort[]>(defaultSort);
  const {
    rowSelection,
    setRowSelection,
    bulkSelectionProps,
    isSelectedOnly,
    deselectAll,
    rowSelectionCount: rowSelected
  } = useBulkSelection();
  const [openEditRateModal, setOpenEditRateModal] = useState(false);
  const [openAdjustRateModal, setOpenAdjustRateModal] = useState(false);
  const [openFinalizePricingModal, setOpenFinalizePricingModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>[]>([]);
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const { data, loading } = useQuery<DuesRatesData>(GET_DUES_RATES_QUERY, {
    variables: {
      filters: {
        ...variables
      }
    },
    fetchPolicy: "cache-first"
  });

  const hasNationalRatesRef = useRef(false);
  const tableData: DuesRatesRow[] = sortBy(
    orderByLodash(
      // TODO: add warnings when backend is ready
      formatTableData({
        data: data?.duesRates?.nodes || [],
        errors,
        warnings: [],
        currentYear: new Date().getFullYear()
      }),
      "classificationNumber"
    ),
    (item) => [item.classificationLetter]
  );
  const totalDuesRates = get(data, "totalDuesRates.totalCount", 0);
  useEffect(() => {
    if (totalDuesRates > 0) {
      hasNationalRatesRef.current = true;
    }
  }, [totalDuesRates]);
  const [updateDuesRates] = useMutation(UPDATE_DUES_RATES_MUTATION);
  const [finalizeDuesRates, { loading: finalizeLoading }] = useMutation(DUES_FINALIZE_RATES_MUTATION);
  const currentYear = new Date().getFullYear();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const handleSort = (updaterOrValue: Updater<ColumnSort[]>) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const filters = {
    classifications: selectedClassificationValues.length > 0
  };
  const hasActiveFilters = filters.classifications || !!debouncedSearch;
  const classificationNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationNodes?.map((node: ClassificationType) => ({
      value: node.name,
      label: node.name
    })) || [];

  useEffect(() => {
    setVariables((prevVariables) => ({
      ...prevVariables,
      search: debouncedSearch || undefined,
      classification: selectedClassificationValues.length > 0 ? selectedClassificationValues : undefined,
      ...defaultVariables
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedClassificationValues]);

  useEffect(() => {
    if (rowSelected === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelected, isSelectedOnly, deselectAll]);
  const filteredData = tableData.filter((item) => rowSelection[item.id]);

  const handleBulkEditRate = async (value: string) => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: selectedIds,
          priceCents: value
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        setOpenEditRateModal(false);
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          BILLING_ANALYTICS_EVENTS.duesRatesUpdated,
          getBillingAnalyticsData({
            source: "dues_rates",
            rateCount: selectedIds.length,
            action: "bulk_edit"
          })
        );
        if (isCompactLayout && rowSelected === 1) {
          deselectAll();
        }
        setErrors((prev) => filterErrorsByKeys(prev, selectedIds));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleBulkAdjustRate = async (value: string) => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: selectedIds,
          percent: Number(value)
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        setOpenAdjustRateModal(false);
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          BILLING_ANALYTICS_EVENTS.duesRatesUpdated,
          getBillingAnalyticsData({
            source: "dues_rates",
            rateCount: selectedIds.length,
            action: "bulk_adjust"
          })
        );
        setErrors((prev) => filterErrorsByKeys(prev, selectedIds));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleEditRate = (value: string, cell: Cell<DuesRatesRow, unknown>) => {
    updateDuesRates({
      variables: {
        input: {
          duesRateIds: [cell.row.id],
          priceCents: Number(value.replace(/[$.]/g, ""))
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesBulkEditRates?.success) {
        showSnackbar("Rate(s) have been updated");
        trackEvent(
          BILLING_ANALYTICS_EVENTS.duesRatesUpdated,
          getBillingAnalyticsData({
            source: "dues_rates",
            rateCount: 1,
            action: "inline_edit"
          })
        );
        setErrors((prev) => prev.filter((error) => !Object.keys(error).includes(cell.row.id)));
      } else {
        setErrorMessage(response.data?.duesBulkEditRates?.message || "Failed to update dues rates");
      }
    });
  };

  const handleFinalizePricing = async () => {
    finalizeDuesRates({
      variables: {
        input: {
          kind: "national"
        }
      },
      refetchQueries: [
        {
          query: GET_DUES_RATES_QUERY,
          variables: {
            filters: {
              ...variables
            }
          }
        }
      ]
    }).then((response) => {
      if (response.data?.duesFinalizeRates?.success) {
        showSnackbar(`${currentYear} National Dues finalized`);
        trackEvent(
          BILLING_ANALYTICS_EVENTS.duesRatesFinalized,
          getBillingAnalyticsData({
            source: "dues_rates",
            action: "finalize_national"
          })
        );
      }
      if (response.data?.duesFinalizeRates?.errors) {
        setErrors(response.data?.duesFinalizeRates?.errors);
      }
    });
  };

  const columns = useMemo(() => {
    return getColumns({
      tableData,
      rowSelected,
      handleEditRate,
      canManageDuesRates: canEdit && canManageDuesRates,
      setOpenEditRateModal,
      setRowSelection,
      currentYear
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, rowSelected]);

  const isFinalized = tableData.length > 0 ? tableData.every((item) => item.finalized) : false;
  const hasSelectableRows = tableData.some((item) => !item.finalized);

  const showFinalizeAction =
    (totalDuesRates > 0 || hasNationalRatesRef.current) && canManageDuesRates && canFinalize && !isFinalized;
  const tableHeadingActions = useMemo(
    () =>
      showFinalizeAction
        ? [{ label: "Finalize Pricing", icon: "lock" as const, onClick: () => setOpenFinalizePricingModal(true) }]
        : [],
    [showFinalizeAction]
  );

  const tableHeadingBody = useMemo(() => {
    const period = `${currentYear} - ${currentYear + 1}`;

    if (!canManageDuesRates || !canFinalize || !canEdit) {
      return "You are not authorized to edit National dues.";
    }

    if (totalDuesRates === 0 && !loading) {
      return `No rates for National annual dues and fees for ${period} were found.`;
    }

    if (isFinalized) {
      return `National annual dues and fees for ${period} have been finalized. Pricing can no longer be adjusted.`;
    }

    return `Please set PGA National’s annual dues and fee amounts below for July 1, ${currentYear} – June 30, ${
      currentYear + 1
    }. Previous annual prices are shown for convenience and encompass July 1st of the applicable year through June 30th of the following. Once pricing is finalized, Sections will be notified to set their rates. Rate changes are saved immediately, but remain editable until finalized.`;
  }, [canManageDuesRates, canFinalize, canEdit, isFinalized, totalDuesRates, currentYear, loading]);

  return (
    <>
      <TableHeading heading="National Annual Dues and Fees" body={tableHeadingBody} actions={tableHeadingActions} />
      <Table
        data={isSelectedOnly ? filteredData : tableData}
        loading={loading}
        onSortingChange={handleSort}
        enableRowSelection={(row) => !row.original.finalized}
        sortingState={orderBy}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        columns={columns}
        renderControls={(table, { onSortClick }) => (
          <TableControls
            useExpandedFilters
            expandedFiltersState={filters}
            onClearAllFilters={() => {
              setVariables(defaultVariables);
              setSelectedClassificationValues([]);
              clearSearch();
              deleteSearchParams("classifications[]");
            }}
            activeFilters={selectedClassificationValues.length > 0 ? 1 : 0}
            onSortClick={onSortClick}
            {...(hasSelectableRows && {
              bulkActions: [
                {
                  label: "Edit Rate",
                  variant: "outlined",
                  onClick: () => setOpenEditRateModal(true)
                },
                {
                  label: "Adjust Rate by %",
                  variant: "outlined",
                  onClick: () => setOpenAdjustRateModal(true)
                }
              ],
              ...bulkSelectionProps,
              onDeselectAll: deselectAll,
              totalRowCount: tableData.length,
              onToggleAllRows: (checked: boolean) => table.toggleAllRowsSelected(checked),
              allRowsSelected: table.getIsAllRowsSelected(),
              someRowsSelected: table.getIsSomeRowsSelected()
            })}
          >
            <SearchInput
              placeholder="Search Product"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <div data-name="classifications">
              <MultiSelect
                label="Classification"
                selectedValues={selectedClassificationValues}
                options={classificationDataNodes}
                onSelect={(value, checked) => {
                  const classification = checked
                    ? [...selectedClassificationValues, value]
                    : selectedClassificationValues.filter((v) => v !== value);
                  setSelectedClassificationValues(classification);
                  if (classification.length > 0) {
                    addSearchParam("classifications[]", classification.join(","));
                  } else {
                    deleteSearchParams("classifications[]");
                  }
                }}
                onClear={() => {
                  setSelectedClassificationValues([]);
                  deleteSearchParams("classifications[]");
                }}
                className="w-full"
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => <TableFooter totalRowCount={tableData.length} {...bulkSelectionProps} />}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Annual National Rates"
            caption={`No dues rates found for ${currentYear}-${currentYear + 1}.`}
            iconName="request_quote"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
        )}
      />
      {openEditRateModal && (
        <EditRateModal
          onDismiss={() => {
            setOpenEditRateModal(false);
            if (isCompactLayout && rowSelected === 1) {
              deselectAll();
            }
          }}
          handleBulkEditRate={handleBulkEditRate}
        />
      )}
      {openAdjustRateModal && (
        <AdjustRateModal
          onDismiss={() => {
            setOpenAdjustRateModal(false);
          }}
          handleBulkAdjustRate={handleBulkAdjustRate}
        />
      )}
      {openFinalizePricingModal && (
        <FinalizePricingModal
          errors={errors}
          onDismiss={() => {
            setOpenFinalizePricingModal(false);
          }}
          handleFinalizePricing={handleFinalizePricing}
          headline={`Finalize ${currentYear} Pricing`}
          buttonText="Finalize Pricing"
          description={`Are you sure you wish to finalize National Dues and Fees for Jul 1, ${currentYear} - Jun 30, ${
            currentYear + 1
          }?
        You should notify Sections that national dues have been set and that they can now finalize their rates. This
        action cannot be undone.`}
          successDescription={`Thank you for submitting dues rates for the upcoming membership year. Sections have been notified that their
            rates are now available for editing and finalization.`}
          loading={finalizeLoading}
          success={tableData.every((item) => item.finalized)}
        />
      )}
    </>
  );
}
